import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, leftIcon, rightIcon, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? props.name ?? generatedId;
    const descriptionId = `${inputId}-description`;
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1.5 block text-label-md text-on-surface">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-subtle">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'h-10 w-full rounded-lg border bg-card px-3 text-data-md text-on-surface shadow-xs',
              // A placeholder has to be readable, not decorative: /70 ink was
              // 3.7:1. on-surface-subtle is the lightest AA-safe step (#667085,
              // 4.98:1) and still clearly reads as a hint, not a value.
              'placeholder:text-on-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:bg-surface-variant disabled:opacity-60',
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              error ? 'border-destructive focus-visible:ring-destructive/40' : 'border-input',
              className,
            )}
            aria-invalid={!!error}
            step={props.step ?? (props.type === 'number' ? 'any' : undefined)}
            autoComplete="off"
            {...props}
            aria-describedby={[props['aria-describedby'], (error || hint) ? descriptionId : undefined].filter(Boolean).join(' ') || undefined}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-subtle">
              {rightIcon}
            </span>
          )}
        </div>
        {error ? (
          <p id={descriptionId} role="alert" className="mt-1 text-body-sm text-destructive">{error}</p>
        ) : hint ? (
          <p id={descriptionId} className="mt-1 text-body-sm text-on-surface-subtle">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
